import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Preview as PreviewIcon,
  Code as CodeIcon,
  Save as SaveIcon,
  Palette as PaletteIcon,
  Business as BrandIcon
} from '@mui/icons-material';
import api from '../services/api';
import NewsletterTabs from '../components/molecules/NewsletterTabs/NewsletterTabs';

const TemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [template, setTemplate] = useState({
    name: '',
    description: '',
    type: 'newsletter',
    html: '',
    css: '',
    variables: [],
    isDefault: false,
    isActive: true,
    allowedBrands: []
  });

  useEffect(() => {
    loadTemplates();
    loadBrands();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/newsletter/templates');
      setTemplates(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const loadBrands = async () => {
    try {
      const response = await api.get('/brands');
      setBrands(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplate({
      name: '',
      description: '',
      type: 'newsletter',
      html: getDefaultTemplate(),
      css: getDefaultCSS(),
      variables: [],
      isDefault: false,
      isActive: true,
      allowedBrands: []
    });
    setDialogOpen(true);
  };

  const handleEditTemplate = (templateItem) => {
    setEditingTemplate(templateItem);
    setTemplate({
      ...templateItem,
      allowedBrands: templateItem.allowedBrands || []
    });
    setDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    try {
      setLoading(true);
      if (editingTemplate) {
        await api.put(`/newsletter/templates/${editingTemplate._id}`, template);
      } else {
        await api.post('/newsletter/templates', template);
      }
      setDialogOpen(false);
      loadTemplates();
    } catch (error) {
      console.error('Erro ao salvar template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este template?')) {
      try {
        await api.delete(`/newsletter/templates/${id}`);
        loadTemplates();
      } catch (error) {
        console.error('Erro ao excluir template:', error);
      }
    }
  };

  const getDefaultTemplate = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>{{subject}}</title>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 20px; text-align: center; }
    .content { padding: 20px; background: #f9f9f9; }
    .article { background: white; margin: 20px 0; padding: 20px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
    .article h3 { color: #333; margin-top: 0; }
    .article p { color: #666; }
    .article a { color: #667eea; text-decoration: none; font-weight: bold; }
    .footer { text-align: center; padding: 20px; color: #666; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Mensure Monitor</h1>
      <h2>{{subject}}</h2>
    </div>
    
    <div class="content">
      <p>Olá {{recipientName}},</p>
      
      {{#if message}}
      <p>{{message}}</p>
      {{/if}}
      
      <h3>Artigos Selecionados:</h3>
      
      {{#each articles}}
      <div class="article">
        <h3>{{title}}</h3>
        {{#if description}}
        <p>{{description}}</p>
        {{/if}}
        <p><a href="{{publicUrl}}">Ler artigo completo →</a></p>
      </div>
      {{/each}}
      
      <p>Obrigado por acompanhar o Mensure Monitor!</p>
    </div>
    
    <div class="footer">
      <p>Este email foi enviado pelo Mensure Monitor</p>
      <p><a href="{{unsubscribeUrl}}">Cancelar inscrição</a></p>
    </div>
  </div>
</body>
</html>`;
  };

  const getDefaultCSS = () => {
    return `
/* Estilos personalizados para o template */
.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.article {
  transition: transform 0.2s ease;
}

.article:hover {
  transform: translateY(-2px);
}

.footer {
  border-top: 1px solid #eee;
  margin-top: 20px;
}`;
  };

  const renderTemplatePreview = () => {
    if (!template.html) return null;
    const previewDoc = template.html.replace(/\{\{.*?\}\}/g, '[VARIÁVEL]');
    
    return (
      <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, bgcolor: '#f9f9f9' }}>
        <Typography variant="subtitle2" gutterBottom>
          Preview do Template:
        </Typography>
        <Box 
          sx={{ 
            maxHeight: 400, 
            overflow: 'auto',
            border: '1px solid #ccc',
            bgcolor: 'white',
            p: 2
          }}>
          <iframe
            title="Template Preview"
            srcDoc={previewDoc}
            style={{ width: '100%', height: 360, border: 'none', background: '#fff' }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, fontWeight: 700, color: 'text.primary' }}>
            <PaletteIcon color="primary" />
            Gerenciador de Templates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4.5, mb: 3 }}>
            Crie, edite e personalize a identidade visual das suas newsletters enviadas.
          </Typography>
        </Box>
      </Box>
      <NewsletterTabs />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTemplate}
        >
          Novo Template
        </Button>
      </Box>

      {/* Lista de Templates */}
      <Grid container spacing={3}>
        {templates.length === 0 && !loading && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider', bgcolor: 'transparent' }}>
              <Typography variant="h6" color="text.primary" fontWeight={600} gutterBottom>
                Nenhum template cadastrado
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 3 }}>
                Você ainda não possui templates ou eles estão ocultos. Comece criando o seu primeiro design!
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateTemplate}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Criar Primeiro Template
              </Button>
            </Paper>
          </Grid>
        )}
        {templates.map((template) => (
          <Grid item xs={12} md={6} lg={4} key={template._id}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 3 } }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6">
                    {template.name}
                  </Typography>
                  <Box>
                    {template.isDefault && <Chip label="Padrão" size="small" color="primary" sx={{ mr: 1 }} />}
                    {!template.isActive ? <Chip label="Inativo" size="small" color="error" /> : <Chip label="Ativo" size="small" color="success" />}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph sx={{ minHeight: 40, mb: 1 }}>
                  {template.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tipo: {template.type} • {template.variables.length} variáveis
                </Typography>
                {(!template.allowedBrands?.length && !template.allowedUsers?.length) && (
                  <Chip label="Público" size="small" color="info" sx={{ mt: 1, fontSize: '0.7rem' }} />
                )}
                {template.allowedBrands && template.allowedBrands.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {template.allowedBrands.slice(0, 3).map((brand, idx) => (
                      <Chip 
                        key={idx} 
                        label={typeof brand === 'object' ? brand.name : 'Marca'} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                    {template.allowedBrands.length > 3 && (
                      <Chip 
                        label={`+${template.allowedBrands.length - 3}`} 
                        size="small" 
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                )}
                {(!template.allowedBrands || template.allowedBrands.length === 0) && (
                  <Chip 
                    label="Apenas Admin" 
                    size="small" 
                    color="warning" 
                    sx={{ mt: 1, fontSize: '0.7rem' }}
                  />
                )}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<PreviewIcon />}
                  onClick={() => {
                    setTemplate(template);
                    setPreviewDialogOpen(true);
                  }}
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditTemplate(template)}
                >
                  Editar
                </Button>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteTemplate(template._id)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog de Edição */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingTemplate ? 'Editar Template' : 'Novo Template'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label="Configurações" />
              <Tab label="HTML" icon={<CodeIcon />} />
              <Tab label="CSS" icon={<PaletteIcon />} />
              <Tab label="Preview" icon={<PreviewIcon />} />
            </Tabs>

            {activeTab === 0 && (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nome do Template"
                      value={template.name}
                      onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        value={template.type}
                        onChange={(e) => setTemplate({ ...template, type: e.target.value })}
                      >
                        <MenuItem value="newsletter">Newsletter</MenuItem>
                        <MenuItem value="promotional">Promocional</MenuItem>
                        <MenuItem value="announcement">Anúncio</MenuItem>
                        <MenuItem value="custom">Personalizado</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Descrição"
                      value={template.description}
                      onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={template.isDefault}
                          onChange={(e) => setTemplate({ ...template, isDefault: e.target.checked })}
                        />
                      }
                      label="Template Padrão"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={template.isActive}
                          onChange={(e) => setTemplate({ ...template, isActive: e.target.checked })}
                        />
                      }
                      label="Ativo"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <BrandIcon fontSize="small" />
                      Marcas Permitidas
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      Selecione as marcas que podrán ver este template. Se nenhuma for selecionada, apenas Admin/Mensure terão acesso.
                    </Typography>
                    <Autocomplete
                      multiple
                      options={brands}
                      getOptionLabel={(option) => option.name}
                      value={brands.filter(b => 
                        template.allowedBrands?.some(ab => 
                          (typeof ab === 'object' ? ab._id : ab) === b._id
                        )
                      )}
                      onChange={(event, newValue) => {
                        setTemplate({ 
                          ...template, 
                          allowedBrands: newValue.map(v => v._id) 
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Selecione as marcas..."
                          size="small"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option._id}>
                          {option.name}
                        </li>
                      )}
                      ChipProps={{
                        size: 'small'
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={20}
                  label="HTML do Template"
                  value={template.html}
                  onChange={(e) => setTemplate({ ...template, html: e.target.value })}
                  sx={{ fontFamily: 'monospace' }}
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  Use variáveis como: {`{{subject}}`, `{{recipientName}}`, `{{message}}`}
                </Alert>
              </Box>
            )}

            {activeTab === 2 && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  label="CSS Personalizado"
                  value={template.css}
                  onChange={(e) => setTemplate({ ...template, css: e.target.value })}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>
            )}

            {activeTab === 3 && (
              <Box sx={{ mt: 3 }}>
                {renderTemplatePreview()}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveTemplate}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Preview */}
      <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, backgroundImage: 'none' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Preview do Template</DialogTitle>
        <DialogContent>
          {(() => {
            const previewDoc = (template.html || '').replace(/\{\{.*?\}\}/g, '[VARIÁVEL]');
            return (
              <Box 
                sx={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 1, 
                  p: 2, 
                  bgcolor: '#f9f9f9',
                  maxHeight: 500,
                  overflow: 'auto'
                }}>
                <iframe
                  title="Template Preview Dialog"
                  srcDoc={previewDoc}
                  style={{ width: '100%', height: 460, border: 'none', background: '#fff' }}
                />
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplateManager;
